<template>
  <BaseCard
    :result="{
      orgName: '',
      orgLink: '',
    }"
    class="ai-answer-card result-card"
  >
    <template #tags>
      <span class="status-tag">
        <Icon
          name="lucide:sparkles"
          size="10"
        />
        {{ statusText }}
      </span>
      <span class="date-tag">
        {{ dateText }}
      </span>
    </template>

    <template #likes-section>
      <button
        v-if="showContinueInChat"
        type="button"
        class="continue-in-chat-btn"
        :aria-label="$t('search.aiAnswer.continueInChatAriaLabel')"
        @click.stop="handleContinueInChat"
      >
        <span>{{ $t('search.aiAnswer.continueInChat') }}</span>
        <Icon name="lucide:arrow-right" />
      </button>
    </template>

    <template #result-content>
      <div
        v-if="aiData.isLoading"
        class="skeleton-loader-container"
      >
        <div class="answer">
          <SkeletonLoader
            width="100%"
            height="14px"
          />
          <SkeletonLoader
            width="60%"
            height="14px"
          />
          <SkeletonLoader
            width="70%"
            height="14px"
          />
        </div>
        <div class="sources">
          <SkeletonLoader
            width="80px"
            height="16px"
            border-radius="99px"
          />
          <SkeletonLoader
            width="120px"
            height="16px"
            border-radius="99px"
          />
        </div>
      </div>
      <div
        v-else
        class="content"
        data-testid="ai-card-content"
      >
        <div v-html="displayedContent" />
        <AiAnswerCardSources
          v-if="!isStreaming && aiData.references.length > 0"
          :sources="aiData.references"
        />
      </div>
    </template>
  </BaseCard>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import BaseCard from './BaseCard.vue';
import SkeletonLoader from '~/components/global/SkeletonLoader.vue';
import AiAnswerCardSources from './AiAnswerCardSources.vue';

const props = defineProps({
  aiData: {
    type: Object,
    default: () => ({
      answer: '',
      references: [],
      status: '',
      isLoading: false,
    }),
  },
});

const { $t, $ld } = useNuxtApp();
const emit = defineEmits(['continueInChat']);

const aiChatEnabled = computed(() => Boolean($ld.flags?.cmsAiChat));
const showContinueInChat = computed(() => aiChatEnabled.value && !props.aiData.isLoading && !isStreaming.value);
const handleContinueInChat = () => {
  emit('continueInChat');
};

const displayedContent = ref('');
const isStreaming = ref(false);

const dateText = computed(() => {
  if (props.aiData.isLoading || isStreaming.value) {
    return '';
  }

  return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
});

const statusText = computed(() => {
  if (props.aiData.isLoading) {
    return `${$t('search.aiAnswer.thinking')}...`;
  }
  if (isStreaming.value) {
    return `${$t('search.aiAnswer.generating')}...`;
  }
  return $t('search.aiAnswer.overview');
});

const streamHtml = (htmlString, displayedHtmlRef) => {
  const tokens = htmlString
    .trim()
    .split(/(\s+|<[^>]*>)/)
    .filter(Boolean);
  let i = 0;

  isStreaming.value = true;

  function nextToken() {
    if (i < tokens.length) {
      displayedHtmlRef.value += tokens[i];
      i++;
      requestAnimationFrame(nextToken);
    } else {
      isStreaming.value = false;
    }
  }
  nextToken();
};

watch(
  () => props.aiData.answer,
  (newAnswer, oldAnswer) => {
    // Allow initial run when oldAnswer is undefined but newAnswer has content
    // or normal runs when oldAnswer exists and values are different
    if ((oldAnswer === undefined && newAnswer) || (oldAnswer !== undefined && newAnswer !== oldAnswer)) {
      displayedContent.value = '';
      streamHtml(newAnswer, displayedContent);
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.ai-answer-card {
  --ai-color: linear-gradient(145deg, #1d62ee 9.46%, #7a14ef 106.88%);

  box-sizing: border-box;
  max-height: 280px;
  overflow: auto;
  border: 1px solid transparent;
  /* Technique to create a gradient border using multiple backgrounds with padding-box and border-box */
  background:
    linear-gradient(var(--color-blue-50), var(--color-blue-50)) padding-box,
    var(--ai-color) border-box;

  .date-tag {
    font-size: 12px;
    font-weight: 400;
    line-height: 1.3;
    color: var(--color-text-secondary);
  }

  .status-tag {
    display: flex;
    align-items: center;
    gap: 4px;
    background: var(--ai-color);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.3;
  }

  .content {
    font-size: 14px;
    color: var(--color-text-secondary);
    line-height: 1.5;
    display: flex;
    flex-direction: column;
    gap: 8px;
    white-space: pre-wrap;
  }

  .skeleton-loader-container {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .answer {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .sources {
      display: flex;
      gap: 8px;
    }
  }

  .continue-in-chat-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    background: var(--ai-color);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    padding: 0;
    border: none;
    cursor: pointer;

    .iconify {
      color: var(--color-blue-500);
    }
  }
}
</style>
